import { DateTime } from "luxon";

/**
 * Reduces a `value` prop to something comparable across renders. Callers that hold the
 * value as a string rebuild the Date on every render, so identity says nothing, and an
 * Invalid Date must not compare as a change or the sync below would never settle.
 */
export function toComparableTime(value?: Date | null) {
  if (!value) return undefined;
  const time = value.getTime();
  return Number.isNaN(time) ? undefined : time;
}

/**
 * DateTimePicker mirrors `value` into state so the popup can move ahead of the parent,
 * and that mirror only seeded at mount — a value arriving later (a form reset fed by an
 * async query) left the trigger showing its placeholder. Comparing against the last
 * value *prop* rather than the mirrored state keeps a selection made inside the popup
 * from being wiped when a caller ignores `onChange`. Returns null when nothing changed.
 */
export function resolveValueChange(
  value: Date | undefined,
  syncedTime: number | undefined,
) {
  const time = toComparableTime(value);
  if (time === syncedTime) return null;
  return { time, displayDate: time === undefined ? undefined : new Date(time) };
}

/**
 * Bridges the picker's Date value to TimeSelect's 24-hour "HH:mm" string: the
 * wall-clock time of `date` as seen in `timeZone`, or "" when there is nothing
 * to show.
 */
export function zonedTimeValue(
  date: Date | null | undefined,
  timeZone: string,
): string {
  if (!date) return "";
  const dt = DateTime.fromJSDate(date).setZone(timeZone);
  return dt.isValid ? dt.toFormat("HH:mm") : "";
}

/**
 * Applies a "HH:mm" wall-clock time to `date` in `timeZone`, keeping the
 * calendar day and zeroing seconds so dropdown-picked times land on the minute.
 */
export function applyTimeValue(date: Date, time: string, timeZone: string): Date {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time);
  if (!match) return date;
  const dt = DateTime.fromJSDate(date).setZone(timeZone);
  if (!dt.isValid) return date;
  return dt
    .set({
      hour: Number(match[1]),
      minute: Number(match[2]),
      second: 0,
      millisecond: 0,
    })
    .toJSDate();
}
